/**
 * Configuration for clustered viewport search behavior.
 * These values are used on both the frontend (deck.gl layers) and backend (OpenSearch queries).
 */
export interface ClusteringConfig {
  /**
   * The geoshape field to use for the intersection of the viewport query.
   */
  intersectField: string;

  /**
   * The geo_point field to use for clustering and centroid calculations.
   */
  clusterField: string;

  /**
   * The minimum precision that can be used for clustering. Precision values
   * below this will be clamped to this value.
   */
  minPrecision: number;

  /**
   * The minimum precision level at which the backend will start returning
   * individual results within a cluster. Below this precision, only cluster
   * counts and centroids are returned (no individual hit details).
   */
  minReturnResultsPrecision: number;

  /**
   * The precision level at which all clusters should be exploded into
   * individual features. This is typically at maximum zoom where users expect
   * to see full detail.
   */
  forceExplodePrecision: number;

  /**
   * This threshold is used to determine the maximum number of features/hits
   * returned from the backend for a single cluster. A cluster can represent
   * more that this number of hits, but will only contain the detail of this
   * number of hits.
   *
   * If a cluster contains fewer features than this threshold, it will be
   * exploded into individual features on the map, providing the zoom level is
   * high enough (determined by `forceExplodePrecision` and
   * `minReturnResultsPrecision`).
   */
  clusterExplosionThreshold: number;
}

export const DEFAULT_CLUSTERING_CONFIG: ClusteringConfig = {
  intersectField: "geometry",
  clusterField: "location",
  minPrecision: 0,
  minReturnResultsPrecision: 12,
  forceExplodePrecision: 20,
  clusterExplosionThreshold: 100,
};
